import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 60, right: 20, bottom: 20, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '260px',
  },
  statsContainer: {
    marginTop: '16px',
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  statBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  statLabel: {
    fontSize: '11px',
    color: '#9ca3af',
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  statValue: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1f2937',
  },
  explanation: {
    marginTop: '12px',
    fontSize: '11px',
    color: '#9ca3af',
    lineHeight: '1.5',
    fontStyle: 'italic',
  },
  legend: {
    marginTop: '12px',
    display: 'flex',
    gap: '12px',
    fontSize: '11px',
    color: '#9ca3af',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  legendSwatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
};

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

export default function RecurrencePlot({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.matrix || !data.dates || data.matrix.length === 0) return;

    const { dates, matrix } = data;
    const n = matrix.length;

    // Determine cell size based on matrix dimensions
    const maxGridSize = 500;
    const cellSize = Math.max(3, Math.min(12, Math.floor(maxGridSize / n)));
    const gridSize = n * cellSize;

    const width = MARGIN.left + gridSize + MARGIN.right;
    const height = MARGIN.top + gridSize + MARGIN.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g');

    // Determine which axis tick indices to show (avoid label overlap)
    const maxLabels = Math.floor(gridSize / 40);
    const tickStep = Math.max(1, Math.ceil(n / maxLabels));
    const tickIndices = [];
    for (let i = 0; i < n; i += tickStep) {
      tickIndices.push(i);
    }

    // ---------------------------------------------------------------
    // X-axis labels (top, rotated)
    // ---------------------------------------------------------------
    tickIndices.forEach((i) => {
      const x = MARGIN.left + i * cellSize + cellSize / 2;
      const y = MARGIN.top - 8;
      g.append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('text-anchor', 'start')
        .attr('transform', `rotate(-45, ${x}, ${y})`)
        .style('font-size', '9px')
        .style('fill', '#6b7280')
        .text(formatDate(dates[i]));
    });

    // ---------------------------------------------------------------
    // Y-axis labels (left)
    // ---------------------------------------------------------------
    tickIndices.forEach((i) => {
      g.append('text')
        .attr('x', MARGIN.left - 8)
        .attr('y', MARGIN.top + i * cellSize + cellSize / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '9px')
        .style('fill', '#6b7280')
        .text(formatDate(dates[i]));
    });

    // ---------------------------------------------------------------
    // Grid border
    // ---------------------------------------------------------------
    g.append('rect')
      .attr('x', MARGIN.left)
      .attr('y', MARGIN.top)
      .attr('width', gridSize)
      .attr('height', gridSize)
      .attr('fill', 'none')
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 1);

    // ---------------------------------------------------------------
    // Recurrence matrix cells
    // ---------------------------------------------------------------
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const val = matrix[i][j];
        if (val === 0) continue; // skip non-recurrence (white background)

        const isDiagonal = i === j;
        const fillColor = isDiagonal ? '#93c5fd' : '#1e3a8a';

        g.append('rect')
          .attr('x', MARGIN.left + j * cellSize)
          .attr('y', MARGIN.top + i * cellSize)
          .attr('width', cellSize)
          .attr('height', cellSize)
          .attr('fill', fillColor)
          .attr('stroke', 'none')
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              i,
              j,
              dateI: dates[i],
              dateJ: dates[j],
              recurrent: val === 1,
              isDiagonal,
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });
      }
    }

    // Draw transparent overlay rects for non-recurrence cells so tooltip works there too
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const val = matrix[i][j];
        if (val !== 0) continue;

        g.append('rect')
          .attr('x', MARGIN.left + j * cellSize)
          .attr('y', MARGIN.top + i * cellSize)
          .attr('width', cellSize)
          .attr('height', cellSize)
          .attr('fill', 'transparent')
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              i,
              j,
              dateI: dates[i],
              dateJ: dates[j],
              recurrent: false,
              isDiagonal: i === j,
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });
      }
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || !data.matrix || !data.dates || data.matrix.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Recurrence Plot</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No recurrence data available
        </div>
      </div>
    );
  }

  const { field, threshold, recurrenceRate, determinism, entropy } = data;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Recurrence Plot</div>
      <div style={styles.subtitle}>
        Visualizing price pattern repetitions over time
        {field ? ` — ${field}` : ''}
        {threshold != null ? ` (threshold: ${(threshold * 100).toFixed(0)}% of price range)` : ''}
      </div>

      <div style={styles.scrollContainer}>
        <svg ref={svgRef} />
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#1e3a8a' }} />
          Recurrence
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#93c5fd' }} />
          Diagonal (self)
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#fff', border: '1px solid #e5e7eb' }} />
          No recurrence
        </span>
      </div>

      {/* Statistics */}
      <div style={styles.statsContainer}>
        {recurrenceRate != null && (
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Recurrence Rate</div>
            <div style={styles.statValue}>{(recurrenceRate * 100).toFixed(1)}%</div>
          </div>
        )}
        {determinism != null && (
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Determinism</div>
            <div style={styles.statValue}>{(determinism * 100).toFixed(1)}%</div>
          </div>
        )}
        {entropy != null && (
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Entropy</div>
            <div style={styles.statValue}>{entropy.toFixed(2)}</div>
          </div>
        )}
      </div>

      {/* Explanation */}
      <div style={styles.explanation}>
        Diagonal structures indicate periodic behavior; vertical/horizontal lines indicate laminar states.
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            ({tooltip.i}, {tooltip.j})
          </div>
          <div style={{ color: '#6b7280', marginBottom: '2px' }}>
            Row: {formatDate(tooltip.dateI)} ({tooltip.dateI})
          </div>
          <div style={{ color: '#6b7280', marginBottom: '4px' }}>
            Col: {formatDate(tooltip.dateJ)} ({tooltip.dateJ})
          </div>
          <div
            style={{
              fontWeight: 600,
              color: tooltip.isDiagonal ? '#3b82f6' : tooltip.recurrent ? '#1e3a8a' : '#9ca3af',
            }}
          >
            {tooltip.isDiagonal
              ? 'Diagonal (trivially recurrent)'
              : tooltip.recurrent
              ? 'Recurrent — similar price state'
              : 'Not recurrent'}
          </div>
        </div>
      )}
    </div>
  );
}
